import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, Btn, Input, Select, Modal, Card, Badge, fmtMoney, fmtDate } from "@/components/ui-kit";
import { Plus, Package } from "lucide-react";

export default function Products() {
  const { t } = useLang();
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [openBatch, setOpenBatch] = useState(null);
  const [batch, setBatch] = useState({ batch_number: "", expiry_date: "", quantity: "" });
  const [openEdit, setOpenEdit] = useState(null);
  const [edit, setEdit] = useState({});

  const load = async () => { const { data } = await api.get("/products"); setProducts(data); };
  useEffect(() => { load(); }, []);

  const addBatch = async (e) => {
    e.preventDefault();
    await api.post(`/products/${openBatch.id}/batches`, { ...batch, quantity: Number(batch.quantity) });
    setOpenBatch(null); setBatch({ batch_number: "", expiry_date: "", quantity: "" });
    await load();
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    const payload = { ...edit };
    ["cost_price", "operational_cost", "pharmacy_price", "public_price", "current_stock"].forEach(k => {
      if (payload[k] != null && payload[k] !== "") payload[k] = Number(payload[k]);
    });
    await api.put(`/products/${openEdit.id}`, payload);
    setOpenEdit(null);
    await load();
  };

  return (
    <div data-testid="products-page">
      <PageHeader title={t("products")} subtitle="Pharmaceutical product catalog & inventory" />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {products.map((p) => {
          const margin = p.public_price > 0 ? ((p.public_price - p.cost_price) / p.public_price * 100).toFixed(0) : 0;
          const lowStock = p.current_stock < 1000;
          return (
            <Card key={p.id} className="overflow-hidden" data-testid={`product-card-${p.sku}`}>
              {/* Product image card - CSS based as per design guidelines */}
              <div
                className="h-44 grid place-items-center text-white relative"
                style={{
                  background: `linear-gradient(135deg, ${p.image_color} 0%, ${p.image_color}dd 100%)`,
                }}
              >
                <div className="text-center">
                  <Package className="w-8 h-8 mx-auto mb-2 opacity-60" strokeWidth={1.5}/>
                  <div className="text-3xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>{p.name}</div>
                  <div className="text-xs uppercase tracking-[0.3em] mt-1 opacity-70 font-bold">{p.sku}</div>
                </div>
                <div className="absolute top-3 end-3">
                  {lowStock ? <Badge color="amber">Low Stock</Badge> : <Badge color="green">In Stock</Badge>}
                </div>
              </div>
              <div className="p-5">
                <div className="text-sm text-slate-600 mb-3 min-h-[40px]">{p.description}</div>
                <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                  <div>
                    <div className="text-xs text-slate-500 uppercase">Pharmacy Price</div>
                    <div className="font-bold text-blue-700 text-lg">{fmtMoney(p.pharmacy_price)} EGP</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 uppercase">Public Price</div>
                    <div className="font-bold text-slate-800 text-lg">{fmtMoney(p.public_price)} EGP</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 uppercase">Stock</div>
                    <div className="font-bold">{p.current_stock} units</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 uppercase">Margin</div>
                    <div className="font-bold text-emerald-700">{margin}%</div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-xs text-slate-500 uppercase">Bonus</div>
                    <div className="font-semibold text-sm">{p.bonus_policy || "-"}</div>
                  </div>
                </div>
                {p.batches && p.batches.length > 0 && (
                  <div className="border-t border-slate-100 pt-3 mb-3">
                    <div className="overline mb-2">Batches</div>
                    <div className="space-y-1 max-h-24 overflow-y-auto">
                      {p.batches.map((b, i) => (
                        <div key={i} className="flex justify-between text-xs">
                          <span className="font-mono">{b.batch_number}</span>
                          <span>Exp: {b.expiry_date}</span>
                          <span className="font-semibold">{b.quantity}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {(user.role === "admin" || user.role === "supervisor") && (
                  <div className="flex gap-2">
                    <Btn variant="secondary" className="flex-1" onClick={() => { setOpenBatch(p); }} data-testid={`add-batch-${p.sku}`}>
                      <Plus className="w-4 h-4"/> Batch
                    </Btn>
                    {user.role === "admin" && (
                      <Btn variant="ghost" className="flex-1" onClick={() => { setOpenEdit(p); setEdit({...p}); }} data-testid={`edit-product-${p.sku}`}>
                        {t("edit")}
                      </Btn>
                    )}
                  </div>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      <Modal open={!!openBatch} onClose={() => setOpenBatch(null)} title="Add Batch">
        <form onSubmit={addBatch} className="space-y-3" data-testid="batch-form">
          <Input label="Batch Number *" required value={batch.batch_number} onChange={(e) => setBatch({...batch, batch_number: e.target.value})}/>
          <Input label="Expiry Date *" type="date" required value={batch.expiry_date} onChange={(e) => setBatch({...batch, expiry_date: e.target.value})}/>
          <Input label="Quantity *" type="number" required value={batch.quantity} onChange={(e) => setBatch({...batch, quantity: e.target.value})}/>
          <div className="flex justify-end gap-2 pt-2">
            <Btn variant="ghost" type="button" onClick={() => setOpenBatch(null)}>{t("cancel")}</Btn>
            <Btn type="submit">{t("save")}</Btn>
          </div>
        </form>
      </Modal>

      <Modal open={!!openEdit} onClose={() => setOpenEdit(null)} title="Edit Product">
        {openEdit && (
          <form onSubmit={saveEdit} className="space-y-3">
            <Input label="Name" value={edit.name || ""} onChange={(e) => setEdit({...edit, name: e.target.value})}/>
            <Input label="Description" value={edit.description || ""} onChange={(e) => setEdit({...edit, description: e.target.value})}/>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Cost Price" type="number" value={edit.cost_price ?? ""} onChange={(e) => setEdit({...edit, cost_price: e.target.value})}/>
              <Input label="Operational Cost" type="number" value={edit.operational_cost ?? ""} onChange={(e) => setEdit({...edit, operational_cost: e.target.value})}/>
              <Input label="Pharmacy Price" type="number" value={edit.pharmacy_price ?? ""} onChange={(e) => setEdit({...edit, pharmacy_price: e.target.value})}/>
              <Input label="Public Price" type="number" value={edit.public_price ?? ""} onChange={(e) => setEdit({...edit, public_price: e.target.value})}/>
            </div>
            <Input label="Bonus Policy" value={edit.bonus_policy || ""} onChange={(e) => setEdit({...edit, bonus_policy: e.target.value})}/>
            <div className="flex justify-end gap-2 pt-2">
              <Btn variant="ghost" type="button" onClick={() => setOpenEdit(null)}>{t("cancel")}</Btn>
              <Btn type="submit">{t("save")}</Btn>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
